"use client";

// L'écosystème Believemy, en bas de la page d'accueil.
//
// Placé après les témoignages et avant le dernier appel à l'action : le
// visiteur a vu les formations, les avis et les prix, donc il sait ce qu'est
// Believemy avant qu'on lui montre ce qui gravite autour. Ce bloc a vécu
// quelques heures en deuxième position, sous le manifeste, et c'était une
// erreur : quatre produits montrés avant même d'avoir expliqué le principal
// se lisent comme quatre portes d'entrée, et le Playground gratuit devient
// une raison de ne pas s'inscrire.
//
// La hiérarchie est portée par la mise en page, pas par une phrase : la carte
// des formations occupe toute la largeur, les trois satellites un tiers
// chacun. Montrer le centre vaut mieux que l'affirmer.
//
// Les satellites sont rangés dans l'ordre du parcours, jamais par importance :
// on découvre, on entretient, on lance. C'est ce qui sépare une progression
// d'un catalogue.
//
// Les clés de traduction gardent le préfixe VisionSection_ hérité de leur
// emplacement d'origine : le dictionnaire envoyé au client sur cette page est
// filtré par préfixe, les renommer demanderait de toucher ce filtre.

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Link } from "@/i18n/navigation";

// Repasser à `false` masque entièrement la section.
const SHOW_ECOSYSTEM = true;

// Reps est la seule adresse interne, d'où le Link localisé : les deux autres
// vivent sur leurs propres domaines.
const ECOSYSTEM = [
    {
        key: "playground",
        name: "Believemy Playground",
        icon: "/images/site/courses/icon-believemyplayground.png",
        href: "https://playground.believemy.com",
        external: true,
    },
    {
        key: "reps",
        name: "Reps",
        icon: "/images/site/courses/icon-reps.png",
        href: "/reps",
        external: false,
    },
    {
        key: "echo",
        name: "Echo",
        icon: "/images/site/assets/echo-icon.png",
        href: "https://talktoecho.com",
        external: true,
    },
];

export default function EcosystemSection() {
    const t = useTranslations();

    if (!SHOW_ECOSYSTEM) return null;

    return (
        <section className="relative max-w-6xl mx-auto px-6 mt-24 md:mt-32 mb-28 md:mb-40">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[32px] bg-[#0a0a0a]"
            >
                {/* Atmospheric glows */}
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                    <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-fuchsia-500/25 blur-[120px]" />
                    <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full bg-sky-500/20 blur-[100px]" />
                    <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[700px] h-[250px] bg-violet-500/15 blur-3xl" />
                </div>

                {/* Grain overlay */}
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
                    }}
                />

                {/* Top accent line */}
                <div
                    aria-hidden
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
                />

                <div className="relative px-8 pt-10 pb-10 sm:px-12 sm:pt-14 sm:pb-12 md:px-16 md:pt-16 md:pb-14">
                    <div className="max-w-2xl">
                        <h2 className="font-sourceSerif text-3xl sm:text-4xl md:text-[2.75rem] leading-[1.2] text-white tracking-tight text-balance">
                            {t("VisionSection_eco_title")}
                        </h2>
                        <p className="mt-5 mb-0 text-[15px] sm:text-base text-white/55 leading-[1.7]">
                            {t("VisionSection_eco_subtitle")}
                        </p>
                    </div>

                    {/* Mise en page latérale : le centre tient toute la
                        hauteur à gauche, les satellites deviennent trois lignes
                        compactes à droite. C'est la seule des trois dispositions
                        essayées où le centre domine sans occuper toute la page,
                        et où les satellites se lisent comme des compléments
                        plutôt que comme trois offres concurrentes. */}
                    <div className="mt-12 md:mt-14 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4 items-stretch">
                        <Link
                            href="/courses"
                            className="group/core flex h-full flex-col rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.13] via-white/[0.05] to-transparent p-7 sm:p-9 transition-colors hover:border-white/25"
                        >
                            {/* Icône d'app reconstruite : le dépôt n'a que la
                                flamme seule, pas de fichier carré avec son fond.
                                Les deux teintes sont celles du dégradé de marque,
                                reprises de believemy-icon-excitation.svg. */}
                            <span className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8511d] to-[#e51578]">
                                <span className="relative block h-8 w-8">
                                    <Image
                                        src="/images/site/brand/believemy-icon-white.svg"
                                        alt=""
                                        fill
                                        sizes="32px"
                                        className="object-contain"
                                    />
                                </span>
                            </span>

                            <span className="mt-6 block text-[11px] uppercase tracking-[0.18em] text-white/40">
                                {t("VisionSection_eco_core_when")}
                            </span>
                            <span className="mt-2 block font-sourceSerif text-3xl sm:text-[2rem] text-white tracking-tight">
                                {t("VisionSection_eco_core_name")}
                            </span>
                            <span
                                aria-hidden
                                className="mt-4 block w-12 h-[2px] bg-primary origin-left transition-transform duration-500 ease-out group-hover/core:scale-x-[2.5]"
                            />
                            <span className="mt-5 block text-[15px] sm:text-base text-white/60 leading-[1.65]">
                                {t("VisionSection_eco_core_description")}
                            </span>
                            <span className="mt-auto pt-7 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white">
                                {t("VisionSection_eco_core_cta")}
                                <SolarIcon name="arrow-right-up-linear" className="w-3.5 h-3.5 transition-transform group-hover/core:translate-x-0.5 group-hover/core:-translate-y-0.5" />
                            </span>
                        </Link>

                        <div className="flex flex-col gap-4">
                            {ECOSYSTEM.map((product, index) => {
                                const inner = (
                                    <>
                                        <span className="relative block h-12 w-12 flex-shrink-0">
                                            <Image
                                                src={product.icon}
                                                alt=""
                                                fill
                                                sizes="48px"
                                                className="object-contain rounded-xl"
                                            />
                                        </span>

                                        <span className="flex min-w-0 flex-col">
                                            <span className="block text-[11px] uppercase tracking-[0.18em] text-white/40">
                                                {t(
                                                    `VisionSection_eco_${product.key}_when`,
                                                )}
                                            </span>
                                            <span className="mt-1 block font-sourceSerif text-xl text-white tracking-tight">
                                                {product.name}
                                            </span>
                                            <span className="mt-2 block text-[14px] text-white/60 leading-[1.6]">
                                                {t(
                                                    `VisionSection_eco_${product.key}_description`,
                                                )}
                                            </span>
                                            <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/80 transition-colors group-hover/eco:text-white">
                                                {t("VisionSection_eco_cta")}
                                                <SolarIcon name="arrow-right-up-linear" className="w-3.5 h-3.5 transition-transform group-hover/eco:translate-x-0.5 group-hover/eco:-translate-y-0.5" />
                                            </span>
                                        </span>
                                    </>
                                );

                                const cardClasses =
                                    "group/eco flex flex-1 items-start gap-4 rounded-3xl border border-white/10 bg-white/[0.05] p-5 transition-colors hover:border-white/25 hover:bg-white/[0.08]";

                                return (
                                    <motion.div
                                        key={product.key}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-40px" }}
                                        transition={{
                                            duration: 0.5,
                                            delay: 0.1 + index * 0.08,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                        className="flex flex-1"
                                    >
                                        {product.external ? (
                                            <a
                                                href={product.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={cardClasses}
                                            >
                                                {inner}
                                            </a>
                                        ) : (
                                            <Link
                                                href={product.href}
                                                className={cardClasses}
                                            >
                                                {inner}
                                            </Link>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </motion.div>
        </section>
    );
}
